import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput } from '@/components/ui/phoneInput'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { type OrgMemberDetails, OrgRole } from '@/models/user'

const ROLE_OPTIONS: { value: OrgRole; label: string }[] = [
  { value: OrgRole.USER, label: 'User' },
  { value: OrgRole.ADMIN, label: 'Admin' },
  { value: OrgRole.BUSINESS_OWNER, label: 'Business Owner' },
]

export const NEW_ORG_MEMBER: OrgMemberDetails = {
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  role: OrgRole.USER,
}

/** The name, contact and role fields staff fill in to add or edit a member of an org. */
export function OrgMemberFields({
  idPrefix,
  details,
  onChange,
  phoneError,
}: {
  idPrefix: string
  details: OrgMemberDetails
  onChange: (details: OrgMemberDetails) => void
  phoneError?: string
}) {
  const update = (patch: Partial<OrgMemberDetails>) => onChange({ ...details, ...patch })

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor={`${idPrefix}-first-name`}>First name</Label>
          <Input
            id={`${idPrefix}-first-name`}
            value={details.firstName}
            onChange={(e) => update({ firstName: e.target.value })}
            required
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={`${idPrefix}-last-name`}>Last name</Label>
          <Input
            id={`${idPrefix}-last-name`}
            value={details.lastName}
            onChange={(e) => update({ lastName: e.target.value })}
            required
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-email`}>Email</Label>
        <Input
          id={`${idPrefix}-email`}
          type="email"
          placeholder="user@example.com"
          value={details.email}
          onChange={(e) => update({ email: e.target.value })}
          required
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-mobile`}>Phone number (optional)</Label>
        <PhoneInput
          id={`${idPrefix}-mobile`}
          value={details.mobile}
          onChange={(value) => update({ mobile: value || '' })}
          error={!!phoneError}
        />
        <FieldError message={phoneError} />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-role`}>Role</Label>
        <Select value={details.role} onValueChange={(value) => update({ role: value as OrgRole })}>
          <SelectTrigger id={`${idPrefix}-role`} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
